import { type JSX } from "solid-js"

type Point = {
  x: number
  y: number
}

type ThinkingLoader = {
  name: string
  kind: "thinking"
  baseRadius: number
  detailAmplitude: number
  petalCount: number
  curveScale: number
  particleCount: number
  trailSpan: number
  durationMs: number
  rotationDurationMs: number
  strokeWidth: number
}

type OrbitLoader = {
  name: string
  kind: "orbit"
  orbitRadius: number
  detailAmplitude: number
  petalCount: number
  curveScale: number
  particleCount: number
  trailSpan: number
  durationMs: number
  rotationDurationMs: number
  strokeWidth: number
}

type RoseLoader = {
  name: string
  kind: "rose"
  roseA: number
  roseBreath: number
  roseK: number
  roseScale: number
  particleCount: number
  trailSpan: number
  durationMs: number
  rotationDurationMs: number
  strokeWidth: number
}

type LoaderVariant = ThinkingLoader | OrbitLoader | RoseLoader

const variants: LoaderVariant[] = [
  {
    name: "Rose Orbit",
    kind: "orbit",
    orbitRadius: 7,
    detailAmplitude: 2.7,
    petalCount: 7,
    curveScale: 2.85,
    particleCount: 72,
    trailSpan: 0.42,
    durationMs: 5200,
    rotationDurationMs: 28000,
    strokeWidth: 6.25,
  },
  {
    name: "Rose Curve",
    kind: "rose",
    roseA: 9.2,
    roseBreath: 0.9,
    roseK: 5,
    roseScale: 3.25,
    particleCount: 78,
    trailSpan: 0.32,
    durationMs: 5400,
    rotationDurationMs: 28000,
    strokeWidth: 6.25,
  },
  {
    name: "Rose Two",
    kind: "rose",
    roseA: 9.2,
    roseBreath: 0.9,
    roseK: 2,
    roseScale: 3.25,
    particleCount: 74,
    trailSpan: 0.3,
    durationMs: 5200,
    rotationDurationMs: 28000,
    strokeWidth: 6.25,
  },
  {
    name: "Rose Three",
    kind: "rose",
    roseA: 9.2,
    roseBreath: 0.9,
    roseK: 3,
    roseScale: 3.25,
    particleCount: 76,
    trailSpan: 0.31,
    durationMs: 5300,
    rotationDurationMs: 28000,
    strokeWidth: 6.25,
  },
  {
    name: "Rose Four",
    kind: "rose",
    roseA: 9.2,
    roseBreath: 0.9,
    roseK: 4,
    roseScale: 3.25,
    particleCount: 78,
    trailSpan: 0.32,
    durationMs: 5400,
    rotationDurationMs: 28000,
    strokeWidth: 6.25,
  },
]

const point = (progress: number, variant: LoaderVariant): Point => {
  const t = progress * Math.PI * 2

  if (variant.kind === "thinking") {
    return {
      x:
        50 +
        (variant.baseRadius * Math.cos(t) - variant.detailAmplitude * Math.cos(variant.petalCount * t)) *
          variant.curveScale,
      y:
        50 +
        (variant.baseRadius * Math.sin(t) - variant.detailAmplitude * Math.sin(variant.petalCount * t)) *
          variant.curveScale,
    }
  }

  if (variant.kind === "orbit") {
    const radius = variant.orbitRadius - variant.detailAmplitude * Math.cos(variant.petalCount * t)
    return {
      x: 50 + Math.cos(t) * radius * variant.curveScale,
      y: 50 + Math.sin(t) * radius * variant.curveScale,
    }
  }

  const radius = variant.roseA * variant.roseBreath * Math.cos(variant.roseK * t)
  return {
    x: 50 + Math.cos(t) * radius * variant.roseScale,
    y: 50 + Math.sin(t) * radius * variant.roseScale,
  }
}

const pointsFor = (variant: LoaderVariant) => Array.from({ length: 97 }, (_, index) => point(index / 96, variant))

const pathFor = (points: Point[]) =>
  points
    .map((item, index) => `${index === 0 ? "M" : "L"} ${item.x.toFixed(2)} ${item.y.toFixed(2)}`)
    .join(" ") + " Z"

const viewBoxFor = (points: Point[], contentScale: number, strokeWidth: number) => {
  const radius = Math.max(
    ...points.map((item) => Math.hypot((item.x - 50) * contentScale, (item.y - 50) * contentScale)),
  )
  const padding = Math.max((strokeWidth * contentScale) / 2, 2.083 * contentScale)
  const edge = radius + padding
  const size = edge * 2

  return {
    size,
    value: `${(50 - edge).toFixed(2)} ${(50 - edge).toFixed(2)} ${size.toFixed(2)} ${size.toFixed(2)}`,
  }
}

const variantIndex = (seed?: string) => {
  if (!seed) return Math.floor(Math.random() * variants.length)
  return [...seed].reduce((hash, char) => (hash * 31 + char.charCodeAt(0)) >>> 0, 0) % variants.length
}

export function MathCurveLoader(props: {
  class?: string
  color?: string
  seed?: string
  sizePx?: number
  strokeWidthPx?: number
  contentScale?: number
}): JSX.Element {
  const variant = variants[variantIndex(props.seed)] ?? variants[0]
  const points = pointsFor(variant)
  const path = pathFor(points)
  const duration = `${variant.durationMs}ms`
  const rotationDuration = `${variant.rotationDurationMs}ms`
  const contentScale = props.contentScale ?? 1
  const baseViewBox = viewBoxFor(points, contentScale, variant.strokeWidth)
  const strokeWidth =
    props.strokeWidthPx && props.sizePx
      ? (props.strokeWidthPx * baseViewBox.size) / (props.sizePx * contentScale)
      : variant.strokeWidth
  const viewBox = viewBoxFor(points, contentScale, strokeWidth)

  return (
    <svg
      aria-hidden="true"
      data-component="math-curve-loader"
      class={`size-6 shrink-0 ${props.class ?? ""}`}
      viewBox={viewBox.value}
      fill="none"
      style={{
        color: props.color ?? "var(--icon-accent-base)",
        ...(props.sizePx ? { width: `${props.sizePx}px`, height: `${props.sizePx}px` } : {}),
      }}
    >
      <g transform={`translate(50 50) scale(${contentScale}) translate(-50 -50)`}>
        <g>
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0 50 50"
            to="360 50 50"
            dur={rotationDuration}
            repeatCount="indefinite"
          />
          <path
            d={path}
            stroke="currentColor"
            stroke-width={strokeWidth}
            stroke-linecap="round"
            stroke-linejoin="round"
            opacity="0.14"
          />
          {Array.from({ length: variant.particleCount }, (_, index) => {
            const progress = variant.particleCount <= 1 ? 0 : index / (variant.particleCount - 1)
            const delay = -(progress * variant.trailSpan * variant.durationMs)
            const opacity = (1 - progress) * 0.68 + 0.18

            return (
              <circle r="2.083" fill="currentColor" opacity={opacity.toFixed(2)}>
                <animateMotion dur={duration} begin={`${delay.toFixed(0)}ms`} repeatCount="indefinite" path={path} />
              </circle>
            )
          })}
        </g>
      </g>
    </svg>
  )
}
